import { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Plus, Target } from 'lucide-react';
import GoalCard from '../components/goals/GoalCard';
import GoalForm from '../components/goals/GoalForm';

export default function Goals() {
  const [showForm, setShowForm]       = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const queryClient = useQueryClient();

  const { data: goals = [], isLoading } = useQuery({
    queryKey: ['goals'],
    queryFn: () => base44.entities.SavingsGoal.list('-created_date'),
  });

  const createGoal = useMutation({
    mutationFn: data => base44.entities.SavingsGoal.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goals'] }); setShowForm(false); },
  });

  const updateGoal = useMutation({
    mutationFn: ({ id, data }) => base44.entities.SavingsGoal.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goals'] }); setShowForm(false); setEditingGoal(null); },
  });

  const deleteGoal = useMutation({
    mutationFn: id => base44.entities.SavingsGoal.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['goals'] }),
  });

  const handleSubmit = (data) => {
    if (editingGoal) {
      updateGoal.mutate({ id: editingGoal.id, data });
    } else {
      createGoal.mutate(data);
    }
  };

  const handleEdit = (goal) => { setEditingGoal(goal); setShowForm(true); };
  const handleCancel = () => { setEditingGoal(null); setShowForm(false); };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Savings Goals</h1>
          <p className="text-sm text-slate-400 mt-0.5">Track your household financial goals</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => { setEditingGoal(null); setShowForm(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New Goal
          </Button>
        )}
      </div>

      {showForm && (
        <GoalForm
          goal={editingGoal}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={createGoal.isPending || updateGoal.isPending}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : goals.length === 0 && !showForm ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="w-7 h-7 text-slate-300" />
          </div>
          <p className="font-semibold text-slate-600 mb-1">No savings goals yet</p>
          <p className="text-sm text-slate-400 mb-5">Set a goal to start tracking your progress</p>
          <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
            <Plus className="w-4 h-4 mr-1.5" /> Add your first goal
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={() => handleEdit(goal)}
              onDelete={id => deleteGoal.mutate(id)}
              onUpdateProgress={amount => updateGoal.mutate({ id: goal.id, data: { current_amount: amount } })}
            />
          ))}
        </div>
      )}
    </div>
  );
}